// src/pages/Users/Techniques/TechniquesView.jsx

import React, { useState } from "react";
import { FaRedo } from "react-icons/fa";

import HeroImage from "../../../components/Hero/HeroImage";
import useScrollToTop from "../../../hooks/useScollToTop";

import useTechniquesController from "./hooks/useTechniquesController";

import TechniquesHero from "./components/TechniquesHero";
import TechniquesToolbar from "./components/TechniquesToolbar";
import TechniqueCard from "./components/TechniqueCard";
import TechniqueSheet from "./components/TechniqueSheet";
import SkeletonCard from "./components/SkeletonCard";
import EmptyState from "./components/EmptyState";
import Uv2TrainingPanel from "./components/Uv2TrainingPanel";

export default function TechniquesView() {
  useScrollToTop();

  const [openedTechnique, setOpenedTechnique] = useState(null);

  const {
    loading,
    error,
    allTechniques,
    filteredTechniques,
    visibleTechniques,
    dynamicOptions,
    ui,
    searchInput,
    shareCopied,
    setSearchInput,
    updateUi,
    resetFilters,
    loadMore,
    copyShareLink,
    reloadTechniques,
  } = useTechniquesController();

  const familiesCount = Math.max(
    (dynamicOptions?.families?.length || 1) - 1,
    0,
  );

  return (
    <section className="min-h-screen bg-slate-50 font-satoshi text-slate-950 dark:bg-slate-950 dark:text-white">
      <div className="pt-[80px] md:pt-[100px]">
        <HeroImage
          title="Techniques Judo"
          subtitle="Consultez, recherchez et révisez les principales techniques de Judo et de Jujitsu."
          imageUrl="/img/cover/cover_techniques.jpg"
        />
      </div>

      <main className="mx-auto w-full max-w-[1280px] px-3 pb-24 pt-5 sm:px-5 lg:px-6">
        <TechniquesHero
          totalCount={allTechniques.length}
          filteredCount={filteredTechniques.length}
          familiesCount={familiesCount}
        />

        <Uv2TrainingPanel
          allTechniques={allTechniques}
          onOpenTechnique={setOpenedTechnique}
        />

        <div className="sticky top-[80px] z-30 -mx-3 mt-5 border-y border-slate-200/80 bg-slate-50/95 px-3 py-3 backdrop-blur-xl dark:border-slate-800 dark:bg-slate-950/95 md:static md:mx-0 md:border-0 md:bg-transparent md:px-0 md:py-0 md:backdrop-blur-0">
          <TechniquesToolbar
            dynamicOptions={dynamicOptions}
            ui={ui}
            searchInput={searchInput}
            shareCopied={shareCopied}
            filteredCount={filteredTechniques.length}
            setSearchInput={setSearchInput}
            onChange={updateUi}
            onReset={resetFilters}
            onShare={copyShareLink}
          />
        </div>

        {error ? (
          <div className="mt-5 rounded-[1.75rem] border border-red-200 bg-red-50 p-5 text-red-900 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-100">
            <h2 className="font-bold">
              Impossible de charger les techniques
            </h2>

            <p className="mt-1 text-sm leading-6 opacity-80">
              {error}
            </p>

            <button
              type="button"
              onClick={reloadTechniques}
              className="mt-4 inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl bg-red-700 px-4 text-sm font-bold text-white transition hover:bg-red-800 active:scale-95"
            >
              <FaRedo />
              Réessayer
            </button>
          </div>
        ) : null}

        <section className="mt-5">
          {loading ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {Array.from({ length: 12 }).map((_, index) => (
                <SkeletonCard key={index} />
              ))}
            </div>
          ) : null}

          {!loading &&
          !error &&
          filteredTechniques.length === 0 ? (
            <EmptyState onReset={resetFilters} />
          ) : null}

          {!loading &&
          !error &&
          filteredTechniques.length > 0 ? (
            <>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                {visibleTechniques.map((technique) => (
                  <TechniqueCard
                    key={technique.id}
                    technique={technique}
                    onOpen={() =>
                      setOpenedTechnique(technique)
                    }
                  />
                ))}
              </div>

              {visibleTechniques.length <
              filteredTechniques.length ? (
                <div className="mt-8 flex flex-col items-center gap-3">
                  <p className="text-xs font-semibold text-slate-400">
                    {visibleTechniques.length} technique
                    {visibleTechniques.length > 1 ? "s" : ""} sur{" "}
                    {filteredTechniques.length}
                  </p>

                  <button
                    type="button"
                    onClick={loadMore}
                    className="inline-flex min-h-12 items-center justify-center rounded-2xl bg-slate-950 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-slate-950/10 transition hover:-translate-y-0.5 hover:bg-slate-800 active:translate-y-0 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200"
                  >
                    Charger plus de techniques
                  </button>
                </div>
              ) : null}
            </>
          ) : null}
        </section>
      </main>

      {openedTechnique ? (
        <TechniqueSheet
          technique={openedTechnique}
          onClose={() => setOpenedTechnique(null)}
        />
      ) : null}
    </section>
  );
}